/* globals Conf */
import React from 'react'
import { action, observable, toJS, computed, autorun } from 'mobx'
import * as defaultConst from '../../constants/default_constants'
import ListStore from 'react-mobx-admin/new_src/store/list'
import ManipStore from 'react-mobx-admin/new_src/store/manip'

class BaseManipStore extends ManipStore {
  //
  constructor (store) {
    super()
    this.store = store
  }

  entityName = 'citem'
  updatePingCount = 0

  onLoaded (record) {
    super.onLoaded(record)
    this.cancelAutorun = autorun(this.flagEditing.bind(this))
  }

  load (id) {
    this.cancelAutorun = autorun(this.flagEditing.bind(this))
    return this.requiredOptions && this.requiredOptions.length > 0
      ? this.store.loadAllOptions(this.requiredOptions).then(() => {
        return super.load(id)
      })
      : super.load(id)
  }

  loadEntry (id) {
    const entityname = this.store.router.params.entityname
    return this.store.requester.getEntry(entityname, id)
  }

  saveEntry (data) {
    const { entityname, id } = this.store.router.params.entityname
    return this.store.requester.saveEntry(entityname, data, data.id)
  }

  onSaved (saved) {
    this.store.addMessage('successfully saved', 'success', 2000)
    this.flagEditDone()
    if (this.store.router.params.id === '_new') {
      this.store.router.params.id = saved.id
      return this.load(saved.id)
    }
    super.onSaved(saved)
  }

  lengthValidator (val, max) {
    if (!val || val.length === 0) {
      return 'value must be provided'
    }
    if (max && val.length > max) {
      return 'value too long'
    }
  }

  // change state is/not edit
  getRecordState () {
    return this.store.requester
      .call(`/state/${this.entityName}/${this.store.router.params.id}`, 'get')
  }

  flagEditing () {
    const record = JSON.stringify(this.record) // needed to be recalled on each record change
    if (record.length > 2 && this.isEntityChanged && !this.recordState) {
      this.recordState = this.getRecordState().then(res => {
        if (res.data && res.data.length > 0) {
          const m = `${res.data[0]} is editing this item, risk of mutual data overwrite! Continue?`
          const realyContinue = confirm(m)
          if (!realyContinue) {
            const { entname, eid } = this.store.router.params
            return this.store.goTo('contractitems', { entname: entname, id: eid })
          }
        }
        this.recordState = res.data
        this.flagEditStart()
      })
    } else if (!this.isEntityChanged && this.recordState) {
      this.flagEditDone()
    } else if (this.recordState) {
      this.flagEditContinue()
    }
  }

  flagEditStart () {
    if (this.store.router.params.id && this.store.router.params.id !== '_new' && this.store.router.params.eid) {
      this.store.requester
        .call(`/state/${this.entityName}/${this.store.router.params.id}`, 'get')
        .catch(this.store.onRequesterError.bind(this.store))
        .then(result => {
          if (!result.data || (result.data && result.data.length <= 0)) {
            this.store.requester.call(`/state/${this.entityName}/${this.store.router.params.id}`, 'post')
              .catch(_ => {
                this.lockAcqiereFailed = true
                this.store.onRequesterError.bind(this.store)
              })
          }
        }).catch(this.store.onRequesterError.bind(this.store))
    }
  }

  flagEditDone () {
    delete this.recordState // have returned to starting state
    delete this.lockAcqiereFailed
    this.store.requester.call(`/state/${this.entityName}/${this.store.router.params.id}`, 'delete')
      .catch(this.store.onRequesterError.bind(this.store))
    clearInterval(this._sendFlagEditInterval)
  }

  flagEditContinue () {
    this.updatePingCount++
    if (!this._sendFlagEditInterval) {
      const fn = this._sendFlagEditContinue.bind(this)
      this._sendFlagEditInterval = setInterval(fn, 10 * 1000)
    }
  }

  _sendFlagEditContinue () {
    if (this.updatePingCount === 0) return // send FlagEditContinue only once per min
    this.store.requester.call(`/state/${this.entityName}/${this.store.router.params.id}`, 'put')
      .then(() => this.updatePingCount = 0)
      .catch(err => {})
  }
}
export { BaseManipStore }

class BaseBatchStore extends BaseManipStore {
  save (mergeFn = null) {
    const parent = this.store
    const store = parent.store
    const batch = toJS(this.record)
    const promises = parent.selection.map((selected) => {
      const id = parent.items[selected][parent.pkName]
      return store.requester.getEntry(parent.entityname, id)
        .then(loaded => {
          mergeFn
            ? mergeFn(loaded, batch)
            : this.updateWithBatch(loaded, batch) // update it with this.record
          return store.requester.saveEntry(parent.entityname, loaded, id)
        })
    })
    // wait for all delete reqests
    return Promise.all(promises).then(this.onDone.bind(this))
  }

  onDone () {
    const parent = this.store
    parent.store.addMessage('successfully saved', 'success', 2000)
    parent.selection = []
    parent.selectionAll = false
    parent.closeBatch()
    return parent.init()
  }

  @observable apply2all = false
  @action switchApply2all () {
    this.apply2all = !this.apply2all
  }

  @action removeFromSelection (idx) {
    this.store.selection.remove(this.store.selection[idx])
    this.runValidators()
  }

  @observable conflictingItems = []
  isConflicting (idx) {
    return this.conflictingItems.indexOf(idx) >= 0
  }
}
export { BaseBatchStore }

class BaseTableStore extends ListStore {
  constructor (store) {
    const router = store.router
    super(router, (newQPars) => {
      router.goTo(router.currentView, router.params, store, newQPars)
    })
    this.store = store
  }

  getEntries (params) {
    return this.store.requester.getEntries(this.entityname, params)
  }

  init () {
    const promises = [
      super.init()
    ]
    if (this.requiredOptions && this.requiredOptions.length > 0) {
      promises.push(this.store.loadAllOptions(this.requiredOptions))
    }
    return Promise.all(promises).then(() => {
      this.store.entityLastState[this.store.cv.entityname] &&
      this.updateQPars(Object.assign(this.store.entityLastState[this.store.cv.entityname], { _perPage: this.store.getUserPreferences('cargo_perPage') })) ||
      this.store.setEntityLastState(this.store.cv.entityname, this.router.queryParams)
    })
  }

  deleteSelected () {
    const promises = this.selection.map((selected) => {
      const id = this.items[selected][this.pkName]
      return this.store.requester.deleteEntry(this.entityname, id)
    })
    // wait for all delete reqests
    return Promise.all(promises).then(this.onDeleted.bind(this))
  }

  @observable currBatchStore = null
  batchAction (action) {
    switch (action) {
    case 'del':
      return this.deleteSelected()
    default:
      const BatchStoreCls = this.batchStores && this.batchStores[action]
      if (BatchStoreCls) {
        this.currBatchStore = new BatchStoreCls(this)
        this.currBatchStore.initNew()
      }
      this.currBatchAction = action // save for switch in particular component
    }
  }

  @action closeBatch () {
    this.currBatchStore = null
  }

  @action onDeleted () {
    this.selection = []
    this.selectionAll = false
    return this._refreshList()
  }

  setPerPage (num) {
    this.router.queryParams._page = 1
    this.store.setUserPreferences('cargo_perPage', num)
    super.setPerPage(num)
  }

  getPerPage () {
    const qp = this.router.queryParams || {}
    return qp._perPage || this.store.getUserPreferences('cargo_perPage') || this.perPage
  }

  setDefaults () {
    const qp = this.router.queryParams || {}
    qp._perPage = qp._perPage ? qp._perPage : this.getPerPage()
    super.setDefaults()
  }

  perPage = Conf.perPage || 50
  perPageOptions = [5, 10, 15, 20, 50, 100, 1000]
}
export { BaseTableStore }
